import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import usePageMeta from '../hooks/usePageMeta';
import {
  ChevronRight, FileText, ExternalLink,
  Mail, Phone, MapPin, User, Building2, ArrowRight,
  BarChart3, Shield, Globe2, Bell, TrendingUp, Star,
  BookOpen, Users, CheckSquare, RefreshCw, Lock,
  AlertCircle, Layers,
} from 'lucide-react';

const API_URL = 'https://www.fundvisercapital.in/app/investor-api.php';

/* ── icon + color per category ID ── */
const CAT_META = {
  '73': { icon: FileText,    color: '#a78bfa', gradient: ['#360153','#5a0280'] },
  '1':  { icon: Bell,        color: '#f59e0b', gradient: ['#92400e','#78350f'] },
  '2':  { icon: TrendingUp,  color: '#10b981', gradient: ['#065f46','#064e3b'] },
  '3':  { icon: Star,        color: '#f59e0b', gradient: ['#92400e','#78350f'] },
  '4':  { icon: BookOpen,    color: '#3b82f6', gradient: ['#1e3a8a','#1e40af'] },
  '5':  { icon: Users,       color: '#8b5cf6', gradient: ['#4c1d95','#5b21b6'] },
  '6':  { icon: CheckSquare, color: '#06b6d4', gradient: ['#164e63','#155e75'] },
  '7':  { icon: RefreshCw,   color: '#f97316', gradient: ['#7c2d12','#9a3412'] },
  '8':  { icon: Shield,      color: '#ec4899', gradient: ['#831843','#9d174d'] },
  '9':  { icon: FileText,    color: '#6b7280', gradient: ['#1f2937','#374151'] },
  '10': { icon: User,        color: '#3b82f6', gradient: ['#1e3a8a','#1e40af'] },
  '11': { icon: Building2,   color: '#10b981', gradient: ['#064e3b','#065f46'] },
  '60': { icon: Lock,        color: '#8b5cf6', gradient: ['#4c1d95','#5b21b6'] },
  '77': { icon: AlertCircle, color: '#ef4444', gradient: ['#7f1d1d','#991b1b'] },
  '79': { icon: Layers,      color: '#0ea5e9', gradient: ['#0c4a6e','#075985'] },
};

const FALLBACK_COLORS  = ['#a78bfa','#f59e0b','#10b981','#3b82f6','#8b5cf6','#06b6d4','#f97316','#ec4899'];
const FALLBACK_GRADS   = [['#360153','#5a0280'],['#92400e','#78350f'],['#065f46','#064e3b'],['#1e3a8a','#1e40af'],['#4c1d95','#5b21b6']];

function getMeta(id, index) {
  return CAT_META[String(id)] || {
    icon: FileText,
    color: FALLBACK_COLORS[index % FALLBACK_COLORS.length],
    gradient: FALLBACK_GRADS[index % FALLBACK_GRADS.length],
  };
}

function countDocs(cat) {
  let n = cat.documents?.length || 0;
  for (const c of cat.children || []) n += countDocs(c);
  return n;
}

/* ── Disclosures under Regulation 46 – hardcoded items ── */
const DISCLOSURES_46 = [
  { title: 'Details of Business',                                               type: 'route', href: '/what-we-do' },
  { title: 'Terms and Conditions of Appointment of Independent Directors',       type: 'pdf',   href: 'https://fundvisercapital.in/uploads/docs/terms-conditions-of-appointment-of-independent-directors-68b150d643337.pdf' },
  { title: 'Composition of various Committees of Board of Directors',            type: 'pdf',   href: 'https://fundvisercapital.in/uploads/docs/composition-of-various-committees-of-bod-68b150e9dca74.pdf' },
  { title: 'Code of Conduct of Board of Directors and Senior Management Personnel', type: 'pdf', href: 'https://fundvisercapital.in/uploads/docs/code-of-conduct-of-bod-senior-management-personnel-68b1515e4d82b.pdf' },
  { title: 'Whistle Blower Policy',                                              type: 'pdf',   href: 'https://fundvisercapital.in/uploads/docs/whistle-blower-policy-68b1518c86559.pdf' },
  { title: 'Criteria of making payments to Non-Executive Directors',             type: 'pdf',   href: 'https://fundvisercapital.in/uploads/docs/remuneration-policy-68b1519ad1af0.pdf' },
  { title: 'Related Party Transactions Policy',                                  type: 'pdf',   href: 'https://fundvisercapital.in/uploads/docs/related-party-transactions-68b151a867e9e.pdf' },
  { title: 'Policy for Determining Material Subsidiaries',                       type: 'pdf',   href: 'https://fundvisercapital.in/uploads/docs/policy-on-material-subsidiaries-68b151b651f71.pdf' },
  { title: 'Familiarization Programmes for Independent Directors',               type: 'pdf',   href: 'https://fundvisercapital.in/uploads/docs/familiarization-programme-68b151c4f2dd6.pdf' },
  { title: 'Grievance Redressal Contact',                                        type: 'route', href: '/contact' },
  { title: 'Board Meeting Notices (Financial Results)',                           type: 'section', keyword: 'announcement' },
  { title: 'Financial Results',                                                  type: 'section', keyword: 'result' },
  { title: 'Annual Reports',                                                     type: 'section', keyword: 'annual report' },
  { title: 'Shareholding Patterns',                                              type: 'section', keyword: 'shareholding' },
  { title: 'Newspaper Advertisements',                                           type: 'section', keyword: 'newspaper' },
  { title: 'Materiality Events Policy',                                          type: 'pdf',   href: 'https://fundvisercapital.in/uploads/docs/materiality-events-policy-68173257e2154.pdf' },
  { title: 'KMP Contact Details',                                                type: 'pdf',   href: 'https://fundvisercapital.in/uploads/docs/contact-details-of-key-managerial-personnel-68b8315c07fea.pdf' },
  { title: 'Statement of Deviations',                                            type: 'section', keyword: 'deviation' },
  { title: 'Annual Returns',                                                     type: 'section', keyword: 'annual return' },
  { title: 'Memorandum & Articles of Association',                               type: 'section', keyword: 'memorandum' },
];

/* ── single document row ── */
const DocRow = ({ doc, accentColor, indent = false }) => (
  <a
    href={doc.file_url}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center justify-between border-b border-gray-100 last:border-b-0 transition-colors group"
    style={{ padding: indent ? '14px 32px 14px 48px' : '16px 24px' }}
    onMouseEnter={e => { e.currentTarget.style.background = `${accentColor}08`; }}
    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
  >
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${accentColor}20` }}>
        <FileText className="w-3.5 h-3.5" style={{ color: accentColor }} />
      </div>
      <span className="text-[13px] sm:text-[15px] font-medium text-gray-800 leading-snug">{doc.title}</span>
    </div>
    <ExternalLink className="w-3.5 h-3.5 flex-shrink-0 ml-3 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" style={{ color: accentColor }} />
  </a>
);

/* ── Disclosures under Regulation 46 panel ── */
const DisclosuresPanel = ({ accentColor, categories, setActiveId }) => {
  const findSection = (keyword) => {
    const kw = keyword.toLowerCase();
    return categories.find(c => c.title.toLowerCase().includes(kw));
  };

  const badge = (icon) => (
    <span className="flex items-center gap-1.5 text-xs font-bold flex-shrink-0 ml-4 px-3 py-1.5 rounded-full"
      style={{ background: `${accentColor}15`, color: accentColor }}>
      Click Here {icon}
    </span>
  );

  return (
    <div className="divide-y divide-gray-100">
      {DISCLOSURES_46.map((item, i) => {
        const iconBox = (
          <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: `${accentColor}20` }}>
            <FileText className="w-3.5 h-3.5" style={{ color: accentColor }} />
          </div>
        );
        const label = (
          <span className="text-[13px] sm:text-[15px] font-medium text-gray-800 leading-snug">{item.title}</span>
        );

        if (item.type === 'pdf') {
          return (
            <a key={i} href={item.href} target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3 min-w-0">{iconBox}{label}</div>
              {badge(<ExternalLink className="w-3 h-3" />)}
            </a>
          );
        }
        if (item.type === 'route') {
          return (
            <Link key={i} to={item.href}
              className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3 min-w-0">{iconBox}{label}</div>
              {badge(<ArrowRight className="w-3 h-3" />)}
            </Link>
          );
        }
        // section — switches sidebar to matching category
        const section = findSection(item.keyword);
        return (
          <button key={i} onClick={() => section && setActiveId(section.id)}
            className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors text-left">
            <div className="flex items-center gap-3 min-w-0">{iconBox}{label}</div>
            {badge(<ArrowRight className="w-3 h-3" />)}
          </button>
        );
      })}
    </div>
  );
};

/* ── recursive category node (accordion) ── */
const CategoryNode = ({ category, accentColor, depth }) => {
  const [open, setOpen] = useState(false);
  const hasChildren = category.children?.length > 0;
  const hasDocs     = category.documents?.length > 0;

  if (!hasChildren && !hasDocs) return null;

  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between hover:bg-gray-50 text-left transition-all duration-200"
        style={{ padding: depth > 0 ? '12px 24px 12px 32px' : '16px 24px' }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: accentColor }} />
          <span className="text-[13px] sm:text-[15px] font-bold text-gray-800 uppercase tracking-wide leading-tight">
            {category.title}
          </span>
        </div>
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200"
          style={{ background: open ? accentColor : 'transparent', border: `1px solid ${open ? accentColor : '#e5e7eb'}` }}
        >
          <ChevronRight
            className="w-3.5 h-3.5 transition-transform duration-200"
            style={{ color: open ? '#fff' : '#9ca3af', transform: open ? 'rotate(90deg)' : 'none' }}
          />
        </div>
      </button>

      {open && (
        <div style={{ background: depth % 2 === 0 ? '#fafbff' : '#f5f6ff' }}>
          {hasChildren && category.children.map(child => (
            <CategoryNode key={child.id} category={child} accentColor={accentColor} depth={depth + 1} />
          ))}
          {hasDocs && category.documents.map(doc => (
            <DocRow key={doc.id} doc={doc} accentColor={accentColor} indent={depth > 0} />
          ))}
        </div>
      )}
    </div>
  );
};

/* ── contact card (Company Secretary / Share Transfer Agent) ── */
const ContactCard = ({ type, accentColor, gradient }) => {
  const isSecretary = type === 'secretary';
  const data = isSecretary
    ? {
        icon: 'person',
        name: 'Mr. Raujesh Khandelwal',
        designation: 'Company Secretary & Compliance Officer',
        fields: [
          { icon: 'map',   label: 'Address', value: '22, 7th Floor, Manek Mahal, 90, Veer Nariman Road, Next to Hotel Ambassador, Churchgate, Mumbai – 400020, Maharashtra, India' },
          { icon: 'phone', label: 'Phone',   value: '+91-22-31236586' },
          { icon: 'mail',  label: 'Email',   value: 'info@fundvisercapital.in' },
        ],
      }
    : {
        icon: 'building',
        name: 'Satellite Corporate Services Pvt. Ltd.',
        designation: 'Registrar & Share Transfer Agent (RTA)',
        fields: [
          { icon: 'map',   label: 'Address', value: 'A 106 & 107, Dattani Plaza, East West Compound, Andheri Kurla Road, Safed Pool Sakinaka, Mumbai – 400072' },
          { icon: 'phone', label: 'Phone',   value: '022-28520461 / 022-28520462' },
          { icon: 'mail',  label: 'Email',   value: 'scs_pl@yahoo.co.in / service@satellitecorporate.com' },
        ],
      };

  return (
    <div className="p-4 sm:p-8">
      <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
        <div className="px-4 sm:px-6 py-5 sm:py-6 flex items-start gap-4" style={{ background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})` }}>
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.18)' }}>
            {data.icon === 'person' ? <User className="w-6 h-6 text-white" /> : <Building2 className="w-6 h-6 text-white" />}
          </div>
          <div>
            <p className="text-white font-black text-lg leading-tight">{data.name}</p>
            <p className="text-white/60 text-sm mt-1">{data.designation}</p>
          </div>
        </div>
        <div className="divide-y divide-gray-100 bg-white">
          {data.fields.map((field, i) => (
            <div key={i} className="flex items-start gap-4 py-4 sm:py-5 px-4 sm:px-6">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `${accentColor}15` }}>
                {field.icon === 'mail'  && <Mail   className="w-4 h-4" style={{ color: accentColor }} />}
                {field.icon === 'phone' && <Phone  className="w-4 h-4" style={{ color: accentColor }} />}
                {field.icon === 'map'   && <MapPin className="w-4 h-4" style={{ color: accentColor }} />}
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1">{field.label}</p>
                {field.icon === 'mail'
                  ? <a href={`mailto:${field.value}`} className="text-sm font-medium hover:underline break-all" style={{ color: accentColor }}>{field.value}</a>
                  : field.icon === 'phone'
                  ? <a href={`tel:${field.value}`} className="text-sm font-medium hover:underline" style={{ color: accentColor }}>{field.value}</a>
                  : <p className="text-sm font-medium text-gray-700 leading-relaxed">{field.value}</p>
                }
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── content panel ── */
const ContentPanel = ({ category, index, categories, setActiveId }) => {
  if (!category) return null;

  const { icon: Icon, color: accentColor, gradient } = getMeta(category.id, index);
  const isSecretary   = String(category.id) === '10';
  const isAgent       = String(category.id) === '11';
  const isContact     = isSecretary || isAgent;
  const isDisclosures = String(category.id) === '79';
  const hasChildren   = category.children?.length > 0;
  const hasDocs       = category.documents?.length > 0;
  const isEmpty       = !isContact && !isDisclosures && !hasChildren && !hasDocs;

  return (
    <div key={category.id} className="section-enter flex flex-col">
      {/* Header */}
      <div
        className="px-5 py-5 sm:px-8 sm:py-7 flex-shrink-0 relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${gradient[0]} 0%, ${gradient[1]} 100%)` }}
      >
        <div className="flex items-start justify-between gap-3 relative z-10">
          <div className="flex items-start gap-3 sm:gap-5 min-w-0">
            <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)' }}>
              <Icon className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg sm:text-2xl font-black text-white leading-tight">{category.title}</h2>
            </div>
          </div>
          <div className="hidden sm:block text-7xl font-black flex-shrink-0 leading-none select-none"
            style={{ color: 'rgba(255,255,255,0.12)' }}>
            {String(index + 1).padStart(2, '0')}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 bg-white">
        {isContact && <ContactCard type={isSecretary ? 'secretary' : 'agent'} accentColor={accentColor} gradient={gradient} />}

        {isDisclosures && <DisclosuresPanel accentColor={accentColor} categories={categories} setActiveId={setActiveId} />}

        {!isContact && !isDisclosures && hasChildren && category.children.map(child => (
          <CategoryNode key={child.id} category={child} accentColor={accentColor} depth={0} />
        ))}

        {!isContact && !isDisclosures && hasDocs && category.documents.map(doc => (
          <DocRow key={doc.id} doc={doc} accentColor={accentColor} />
        ))}

        {isEmpty && (
          <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
            No documents available yet.
          </div>
        )}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════ */
const InvestorInfo = () => {
  usePageMeta(
    "Investor Info",
    "Investor relations, compliance documents, board policies, and key managerial contact details for Fundviser Capital (India) Ltd. (BSE: 530197)."
  );
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [activeId, setActiveId]     = useState(null);
  const [error, setError]           = useState(false);

  useEffect(() => {
    fetch(API_URL)
      .then(res => res.json())
      .then(data => {
        setCategories(data);
        setActiveId(data[0]?.id ?? null);
        setLoading(false);
      })
      .catch(() => { setError(true); setLoading(false); });
  }, []);

  const activeIndex    = categories.findIndex(c => c.id === activeId);
  const activeCategory = categories[activeIndex] ?? null;

  return (
    <div className="min-h-screen" style={{ background: '#f4f6fb' }}>
      <style>{`
        @keyframes sectionEnter { from{opacity:0;transform:translateX(20px)} to{opacity:1;transform:translateX(0)} }
        @keyframes slowZoom     { from{transform:scale(1)} to{transform:scale(1.08)} }
        @keyframes floatA { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-16px)} }
        @keyframes floatB { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-24px)} }
        .section-enter { animation: sectionEnter 0.35s cubic-bezier(0.16,1,0.3,1) both; }
        .slow-zoom     { animation: slowZoom 18s ease-in-out alternate infinite; }
        .float-a       { animation: floatA 7s ease-in-out infinite; }
        .float-b       { animation: floatB 9s ease-in-out infinite; }
      `}</style>

      <Navbar />

      {/* ── HERO ── */}
      <div className="relative pt-20 overflow-hidden"
        style={{ background: 'linear-gradient(135deg,#0a0015 0%,#1a002b 50%,#0a0015 100%)' }}>
        <div className="absolute inset-0 overflow-hidden">
          <img src="/capital_markets.jpg" alt="" className="w-full h-full object-cover slow-zoom" style={{ opacity: 0.08 }} />
        </div>
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,1) 1px,transparent 1px)',
          backgroundSize: '60px 60px',
        }} />
        <div className="float-a absolute top-16 left-20 w-80 h-80 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle,rgba(168,85,247,0.2) 0%,transparent 70%)', filter: 'blur(60px)' }} />
        <div className="float-b absolute bottom-8 right-24 w-72 h-72 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle,rgba(245,158,11,0.16) 0%,transparent 70%)', filter: 'blur(50px)' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
          <span className="inline-block px-5 py-2 bg-white/5 backdrop-blur-md text-yellow-400 rounded-full text-xs font-black tracking-widest uppercase mb-8 border border-white/10">
            BSE Listed · 530197
          </span>
          <h1 className="text-6xl md:text-7xl font-black text-white leading-tight mb-6">
            Investor{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-200">Information</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed mb-14">
            Comprehensive investor resources, regulatory filings, and financial disclosures for Fundviser Capital (India) Ltd.
          </p>

          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {[
              { icon: BarChart3, label: 'BSE Code',  value: '530197'       },
              { icon: Shield,    label: 'Listed On', value: 'BSE Limited'  },
              { icon: Globe2,    label: 'ISIN',      value: 'INE365H01014' },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="flex items-center gap-3 px-4 sm:px-6 rounded-2xl backdrop-blur-md"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', height: 56 }}>
                  <div className="w-8 h-8 rounded-xl bg-yellow-400/10 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-yellow-400" />
                  </div>
                  <div className="text-left">
                    <p className="text-gray-400 text-[10px]">{s.label}</p>
                    <p className="text-white font-black text-sm sm:text-base">{s.value}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <a href="https://www.bseindia.com/stock-share-price/fundviser-capital-(india)-ltd/fundviser/530197/"
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-8 py-4 rounded-2xl text-white font-black text-base transition-all hover:scale-105"
            style={{ background: 'linear-gradient(135deg,#f59e0b,#d97706)', boxShadow: '0 8px 24px rgba(245,158,11,0.35)' }}>
            <BarChart3 className="w-5 h-5" />
            View Live on BSE
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>

        <div className="relative h-12">
          <svg viewBox="0 0 1440 48" className="absolute bottom-0 w-full" preserveAspectRatio="none">
            <path d="M0,48 L1440,48 L1440,0 Q720,48 0,0 Z" fill="#f4f6fb" />
          </svg>
        </div>
      </div>

      {/* ── MAIN LAYOUT ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-20">

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-32 gap-3 text-gray-500">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span className="text-sm font-medium">Loading investor information…</span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-center justify-center py-32 text-red-400 text-sm">
            Failed to load data. Please refresh the page.
          </div>
        )}

        {/* Content */}
        {!loading && !error && (
          <>
            {/* Desktop */}
            <div className="hidden lg:flex rounded-2xl overflow-hidden shadow-2xl border border-gray-200">

              {/* Sidebar */}
              <div className="flex-shrink-0 flex flex-col overflow-y-auto"
                style={{ width: 340, background: '#ffffff', borderRight: '1px solid #e5e7eb', maxHeight: '80vh' }}>
                <div className="flex items-center gap-3 py-5 px-6 flex-shrink-0 sticky top-0 z-10"
                  style={{ borderBottom: '1px solid #e5e7eb', background: '#f8f9ff' }}>
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#7c3aed' }} />
                  <span className="font-black tracking-widest text-xs uppercase" style={{ color: '#4b5563' }}>
                    Investor Information
                  </span>
                </div>

                <nav className="flex-1">
                  {categories.map((cat, i) => {
                    const isActive = cat.id === activeId;
                    const { icon: NavIcon, color } = getMeta(cat.id, i);
                    const total = countDocs(cat);
                    return (
                      <button
                        key={cat.id}
                        onClick={() => setActiveId(cat.id)}
                        className="w-full text-left flex items-center gap-3 py-4 px-5 transition-all duration-200"
                        style={{
                          borderBottom: '1px solid #f3f4f6',
                          borderLeft: `3px solid ${isActive ? color : 'transparent'}`,
                          background: isActive ? `${color}12` : 'transparent',
                        }}
                        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f9fafb'; }}
                        onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ background: isActive ? `${color}20` : '#f3f4f6' }}>
                          <NavIcon className="w-4 h-4" style={{ color: isActive ? color : '#9ca3af' }} />
                        </div>
                        <span className="text-[15px] leading-snug flex-1 min-w-0 truncate"
                          style={{ color: isActive ? '#111827' : '#6b7280', fontWeight: isActive ? 700 : 400 }}>
                          {cat.title}
                        </span>
                        {total > 0 && (
                          <span className="text-[11px] font-black px-2 py-0.5 rounded-full flex-shrink-0"
                            style={{ background: isActive ? `${color}20` : '#f3f4f6', color: isActive ? color : '#9ca3af' }}>
                            {total}
                          </span>
                        )}
                        <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" style={{ color: isActive ? color : '#d1d5db' }} />
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 bg-white overflow-y-auto" style={{ maxHeight: '80vh' }}>
                <ContentPanel category={activeCategory} index={activeIndex} categories={categories} setActiveId={setActiveId} />
              </div>
            </div>

            {/* Mobile */}
            <div className="lg:hidden">
              <div className="mb-4">
                <label className="block text-xs font-black tracking-widest uppercase text-gray-500 mb-2 px-1">
                  Browse Section
                </label>
                <select
                  value={activeId ?? ''}
                  onChange={e => setActiveId(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border bg-white text-gray-700 font-medium text-sm focus:outline-none"
                  style={{ borderColor: '#360153' }}
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.title}</option>
                  ))}
                </select>
              </div>
              <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-200">
                <ContentPanel category={activeCategory} index={activeIndex} categories={categories} setActiveId={setActiveId} />
              </div>
            </div>
          </>
        )}

        {/* BSE Strip */}
        <div className="mt-8 rounded-2xl overflow-hidden shadow-lg"
          style={{ background: 'linear-gradient(135deg,#0a0015 0%,#1a002b 100%)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-5 px-5 sm:px-10 py-6 sm:py-7">
            <div className="flex items-center gap-5">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(245,158,11,0.15)' }}>
                <BarChart3 className="w-7 h-7 text-yellow-400" />
              </div>
              <div>
                <p className="font-black text-white text-lg">Live Stock Price on BSE</p>
                <p className="text-gray-400 text-sm mt-0.5">Fundviser Capital (India) Ltd · BSE: 530197 · ISIN: INE365H01014</p>
              </div>
            </div>
            <a href="https://www.bseindia.com/stock-share-price/fundviser-capital-(india)-ltd/fundviser/530197/"
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-7 py-3.5 rounded-2xl text-white font-black text-base transition-all hover:scale-105 flex-shrink-0"
              style={{ background: 'linear-gradient(135deg,#f59e0b,#d97706)', boxShadow: '0 6px 20px rgba(245,158,11,0.4)' }}>
              View on BSE
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default InvestorInfo;
